import { useEffect, useState } from 'react';
import axios from 'axios';
import { calculateAgeInMonths, getClientId, getLocationId } from '../../utils/index';
import { ALL_EVENTS_GROUP } from '../../constants';
import useSettings from '../useSettings';
import dayjs from 'dayjs';
import { User } from '../../types';

interface UseEventFetchingParams {
  selectedEventGroup: { id: string };
  selectedChild: { dob: string } | null;
  currentPage: number;
  searchQuery: string | null;
  user: User | null;
}

interface Event {
  id: number;
  name: string;
  description: string;
  status: number;
  capacity: number;
  minMonthsRestrictions: number;
  maxMonthsRestrictions: number;
  startDate: string;
  endDate: string;
  registrationStartDate: string;
  registrationEndDate: string;
  occupiedSeats: number;
}

interface UseEventFetchingReturn {
  loading: boolean;
  filteredEvents: Event[];
  totalPages: number;
}

const useEventFetching = ({
  selectedEventGroup,
  selectedChild,
  currentPage,
  searchQuery,
  user,
}: UseEventFetchingParams): UseEventFetchingReturn => {
  const [loading, setLoading] = useState(false);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const events_api_url = import.meta.env.VITE_EVENTS_API_URL;
  const client_id = getClientId();
  const locationId = getLocationId();
  const events_limit = 9;
  const { hideAfterRegistrationClose } = useSettings();

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        let url = `${events_api_url}/events?filter.status=1&filter.isVisible=1`;
        url += `&limit=${events_limit}&page=${currentPage}`;

        if (!isNaN(Number(selectedEventGroup?.id)) && selectedEventGroup?.id != ALL_EVENTS_GROUP.id) {
          url += `&filter.group=${selectedEventGroup?.id}`;
        }
        if (selectedChild) {
          const age = calculateAgeInMonths(selectedChild.dob);
          url += `&filter.ageInMonths=${age}`;
        }
        if (searchQuery) {
          url += `&search=${encodeURIComponent(searchQuery)}`;
        }
        if (hideAfterRegistrationClose) {
          url += `&filter.registrationEndDate=$gte:${dayjs().toISOString()}`;
        }
        if (locationId) {
          url += `&filter.locationId=${locationId}`;
        }

        const response = await axios.get(url, {
          headers: { client_id: client_id },
        });
        let events = response.data.data.data;
        events = events.sort((a: Event, b: Event) => {
          const dateA = new Date(a.startDate);
          const dateB = new Date(b.startDate);
          return dateA.getTime() - dateB.getTime();
        });

        setTotalPages(response.data.data.meta.totalPages);
        setFilteredEvents(events);
      } catch (error) {
        console.error('Error fetching events:', error);
        setFilteredEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [
    selectedEventGroup,
    selectedChild,
    events_api_url,
    currentPage,
    events_limit,
    searchQuery,
    user,
    hideAfterRegistrationClose,
  ]);

  return { loading, filteredEvents, totalPages };
};

export default useEventFetching;
